import React, { useMemo, useState } from 'react';

type Entry = { key: string; status: 'owned'|'wanted'|'missing' };

function normalizeLine(line: string): string | null {
  const t = line.trim();
  if (!t) return null;
  return t;
}

function parseContent(raw: string, fallbackStatus: Entry['status']): Entry[] {
  const text = raw.trim();
  if (!text) return [];

  // Try JSON first
  try {
    const json = JSON.parse(text);
    if (Array.isArray(json)) {
      const out: Entry[] = [];
      for (const item of json) {
        if (typeof item === 'string') {
          const k = normalizeLine(item);
          if (k) out.push({ key: k, status: fallbackStatus });
        } else if (item && typeof item === 'object') {
          const id = String(item.id || item.key || '').trim();
          const variant = item.variant ? String(item.variant).trim() : '';
          const key = id ? (variant ? `${id}|${variant}` : id) : '';
          const status = (item.status === 'owned' || item.status === 'wanted' || item.status === 'missing')
            ? item.status
            : fallbackStatus;
          if (key) out.push({ key, status });
        }
      }
      if (out.length) return out;
    }
  } catch {}

  // Try CSV with headers id,variant,status
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length) {
    const header = lines[0].toLowerCase();
    if (header.includes(',')) {
      const headers = header.split(',').map((h) => h.trim());
      if (headers.includes('id') || headers.includes('key')) {
        const idIdx = headers.indexOf('id') >= 0 ? headers.indexOf('id') : headers.indexOf('key');
        const variantIdx = headers.indexOf('variant');
        const statusIdx = headers.indexOf('status');
        const out: Entry[] = [];
        for (let i = 1; i < lines.length; i += 1) {
          const cols = lines[i].split(',');
          const id = (cols[idIdx] || '').trim();
          const variant = variantIdx >= 0 ? (cols[variantIdx] || '').trim() : '';
          const s = statusIdx >= 0 ? (cols[statusIdx] || '').trim() : '';
          if (!id) continue;
          const key = variant ? `${id}|${variant}` : id;
          const status = (s === 'owned' || s === 'wanted' || s === 'missing') ? (s as Entry['status']) : fallbackStatus;
          out.push({ key, status });
        }
        if (out.length) return out;
      }
    }
  }

  // Fallback: newline list of keys
  const out: Entry[] = [];
  for (const ln of lines) {
    const k = normalizeLine(ln);
    if (k) out.push({ key: k, status: fallbackStatus });
  }
  return out;
}

export default function ImportWizard({ open, onClose, onApply }:
  { open: boolean; onClose: () => void; onApply: (entries: Entry[]) => void; }) {
  const [text, setText] = useState('');
  const [statusMode, setStatusMode] = useState<Entry['status']>('owned');
  const [error, setError] = useState<string | null>(null);

  const entries = useMemo(() => parseContent(text, statusMode), [text, statusMode]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center">
          <div className="font-semibold">Import Cards</div>
          <button onClick={onClose} className="text-sm px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">Close</button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm mb-2">Paste JSON, CSV (id,variant,status), or newline-separated keys (e.g. <code>base2-1</code> or <code>base2-1|reverseHolofoil</code>)</div>
            <textarea value={text} onChange={(e)=>setText(e.target.value)} className="w-full h-48 border rounded p-2 font-mono text-sm" placeholder="id,status\nbase2-1,owned\n..." />
            <div className="flex items-center gap-2 mt-2 text-sm">
              <span>Default status:</span>
              <select value={statusMode} onChange={(e)=>setStatusMode(e.target.value as any)} className="border rounded px-2 py-1">
                <option value="owned">Owned</option>
                <option value="wanted">Wanted</option>
                <option value="missing">Missing</option>
              </select>
            </div>
            {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
          </div>
          <div>
            <div className="font-semibold mb-2">Preview ({entries.length} items)</div>
            <div className="max-h-48 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">key</th>
                    <th className="text-left p-2">status</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.slice(0, 50).map((e, i) => (
                    <tr key={i} className="odd:bg-white even:bg-gray-50">
                      <td className="p-2 font-mono">{e.key}</td>
                      <td className="p-2">{e.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded"
            onClick={() => { setText(''); setError(null); }}
          >
            Clear
          </button>
          <button
            className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded"
            onClick={() => {
              if (!entries.length) { setError('Nothing to import'); return; }
              onApply(entries);
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}


